import type { JSX, ReactNode } from "react";

import { cn } from "@/shared/lib/cn";

/**
 * Builder section card (mockup `.bsection`): a titled white panel with a hairline
 * divider trailing the label. Groups related fields (Basics / Targeting / Rule
 * mode) so the builder reads as discrete sections instead of a flat form.
 */
export function SectionCard({
  title,
  children,
  "data-testid": testId,
}: {
  title: string;
  children: ReactNode;
  "data-testid"?: string;
}): JSX.Element {
  return (
    <section
      data-testid={testId}
      className="rounded-2xl border border-[var(--premium-border)] bg-white p-5 shadow-[var(--sh-card)]"
    >
      <div className="mb-3.5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
        <span>{title}</span>
        <span className="h-px flex-1 bg-[var(--premium-border)]" aria-hidden />
      </div>
      {children}
    </section>
  );
}

export interface RadioPillOption {
  value: string;
  label: ReactNode;
}

/**
 * Radio-pill group (mockup `.radio-pill`): single-select pills with a brand wash
 * + ring on the active option. Keyboard accessible (`role="radio"`). Used for
 * marketplace and rule-mode selection.
 */
export function RadioPillGroup({
  name,
  value,
  options,
  onChange,
  "data-testid": testId,
}: {
  name: string;
  value: string;
  options: RadioPillOption[];
  onChange: (value: string) => void;
  "data-testid"?: string;
}): JSX.Element {
  return (
    <div role="radiogroup" aria-label={name} data-testid={testId} className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const checked = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={checked}
            data-testid={testId ? `${testId}-${opt.value}` : undefined}
            onClick={() => onChange(opt.value)}
            className={cn(
              "inline-flex items-center gap-2 rounded-[var(--control-r)] border px-3.5 py-2 text-[13px] font-semibold transition",
              checked
                ? "border-[var(--brand-500)] bg-[var(--row-selected)] text-[var(--brand-700)] shadow-[0_0_0_3px_color-mix(in_srgb,var(--brand-400)_18%,transparent)]"
                : "border-[var(--premium-border-strong)] bg-white text-[var(--text-secondary)] hover:bg-[var(--canvas)]",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "grid h-3.5 w-3.5 place-items-center rounded-full border-2",
                checked ? "border-[var(--brand-500)]" : "border-[var(--premium-border-strong)]",
              )}
            >
              {checked && <span className="h-1.5 w-1.5 rounded-full bg-[var(--brand-500)]" />}
            </span>
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export interface SegOption {
  value: string;
  label: ReactNode;
}

/**
 * Inline segmented toggle (mockup `.seg-toggle`): a compact two/three-way switch
 * that reads as one control. Drives discount / shipping / gift-limit choices in
 * the rule card without the visual weight of a full dropdown.
 */
export function SegToggle({
  value,
  options,
  onChange,
  "aria-label": ariaLabel,
  "data-testid": testId,
}: {
  value: string;
  options: SegOption[];
  onChange: (value: string) => void;
  "aria-label"?: string;
  "data-testid"?: string;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      className="inline-flex overflow-hidden rounded-lg border border-[var(--premium-border-strong)] bg-white"
    >
      {options.map((opt, i) => {
        const pressed = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={pressed}
            data-testid={testId ? `${testId}-${opt.value}` : undefined}
            onClick={() => onChange(opt.value)}
            className={cn(
              "px-3 py-1.5 text-[12px] font-semibold whitespace-nowrap transition",
              i > 0 && "border-l border-[var(--premium-border-strong)]",
              pressed
                ? "bg-[var(--row-selected)] text-[var(--brand-700)]"
                : "text-[var(--text-secondary)] hover:bg-[var(--canvas)]",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
